import { extractApiErrorMessage } from '@doccareplus/api-client';
import { store } from '../store';
import { addToast, removeToast, type Toast } from '../store/slices/ui.slice';

/**
 * Global transient feedback (snackbar) — the fallback for surfacing outcomes,
 * chiefly API errors, on pages that have no inline place to show them.
 *
 * Backed by the Redux `ui` slice (single source of truth per the app's
 * Redux-only rule); `<ToastHost>` renders the queue. These are plain functions
 * bound to the store singleton so they work anywhere — inside components,
 * event handlers, react-query `onError`, or non-React modules alike.
 *
 *   import { toastApiError } from '@doccareplus/kernel/application';
 *   try { await save(); } catch (err) { toastApiError(err, t('errors.saveFailed')); }
 */

export type ToastType = Toast['type'];

export interface ShowToastInput {
  type?: ToastType;
  message: string;
  /** Auto-dismiss delay in ms; falls back to the Toaster's default when omitted. */
  duration?: number;
}

/** Show a toast. Pass a string for a quick neutral (info) message. */
export function showToast(input: ShowToastInput | string): void {
  const params = typeof input === 'string' ? { message: input } : input;
  store.dispatch(addToast({ type: params.type ?? 'info', message: params.message, duration: params.duration }));
}

/**
 * Show the backend's error message in a destructive toast, falling back to
 * `fallback` when the error carries no meaningful message (network error,
 * thrown string, pre-flight guard).
 */
export function toastApiError(error: unknown, fallback: string, duration?: number): void {
  store.dispatch(addToast({ type: 'error', message: extractApiErrorMessage(error) ?? fallback, duration }));
}

/** Dismiss a specific toast by id. */
export function dismissToast(id: string): void {
  store.dispatch(removeToast(id));
}

/** Hook form for call sites that prefer a hook; returns the same functions. */
export function useToast() {
  return { toast: showToast, toastApiError, dismiss: dismissToast };
}
